/** Third party imports */
import { Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { isEqual } from 'lodash';

/** Component imports */
import { ApiDealers, CreateUserStepsApiService, CreateUserStepsService } from '@core';
import { SelectDealerStepRoles } from '@features/users/interfaces';
import { Checkbox } from '@ntv360/component-pantry';
import { AutocompleteComponent } from '@shared';

@Component({
    selector: 'app-select-dealer',
    imports: [Checkbox, AutocompleteComponent],
    templateUrl: './select-dealer.component.html',
    styleUrl: './select-dealer.component.scss',
})
export class SelectDealerComponent implements OnInit {
    /**
     * Inject create user service so we can save the step data
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Inject create user api service
     */
    private createUserStepsApiService = inject(CreateUserStepsApiService);

    /**
     * Selected user role to be created
     */
    protected selectedUserRole = this.createUserStepsService.selectedUserType;

    /**
     * Whether the searching or loading of dealers is in progress
     */
    protected isLoadingDealers = signal<boolean>(false);

    /**
     * Whether there are selected dealers
     */
    protected hasSelectedDealers = signal<boolean | null>(null);

    /**
     * Error message for this page
     */
    protected errorMessage = signal<string>('Please select at least one dealer');

    /**
     * Dealer list
     */
    protected dealerList = signal<any[]>([]);

    /**
     * User selected dealers
     *
     * A two-way binding using `model` in the autocomplete component. When the selectedOptions updates there, it also updates this signal
     */
    public selectedDealers = signal<{ label: string; value: string }[]>([]);

    /**
     * Computed value whether or not hide the error message on selection change
     */
    protected hideErrorMessage = computed(() => {
        if (this.hasSelectedDealers() !== null) {
            return this.selectedDealers().length > 0 ? true : false;
        }
        return true;
    });

    constructor() {
        /**
         * Hydrate internal states from draft if there are any
         */
        effect(() => {
            const role = this.selectedUserRole()?.id as SelectDealerStepRoles;
            if (!role || role === ('none' as any)) return;

            const draftData = this.createUserStepsService.getDraft(role);
            const savedDealers = draftData['select-dealer']?.selectedDealers ?? [];

            untracked(() => {
                if (!isEqual(savedDealers, this.selectedDealers())) {
                    this.selectedDealers.set(savedDealers);
                }
            });
        });

        /**
         * Effect to update the draft when selected dealers changes
         */
        effect(() => {
            const dealers = this.selectedDealers();
            const role = this.selectedUserRole()?.id as SelectDealerStepRoles;
            if (!role || role === ('none' as any)) return;

            // If all selected dealers are cleared, we disable the next step from getting clicked using the stepper clicks
            // Enable all of them otherwise
            if (dealers.length === 0) {
                this.createUserStepsService.setInvalidStep(
                    this.createUserStepsService.currentStepIndex(),
                );
            } else {
                this.createUserStepsService.setValidStep();
            }

            // Check if draft in service already has this data to avoid redundant updates
            const currentDraft = untracked(() => this.createUserStepsService.getDraft(role));
            const existingDealers = currentDraft['select-dealer']?.selectedDealers ?? [];

            if (!isEqual(dealers, existingDealers)) {
                this.createUserStepsService.patchDraftStep(role, 'select-dealer', {
                    selectedDealers: dealers,
                });
            }
        });
    }

    /**
     * Fetches the list of dealers, temporary for now
     */
    ngOnInit(): void {
        this.loadDealers();
    }

    /**
     * Fetches the list of dealers
     */
    private loadDealers(): void {
        this.isLoadingDealers.set(true);
        this.createUserStepsApiService.getDealers().subscribe({
            next: (dealers) => {
                // Map dealers to fit the autocomplete component format
                const mappedData = this.mapDataToUI(dealers);
                this.dealerList.set(mappedData);
                this.isLoadingDealers.set(false);
            },
            error: (error) => {
                console.error('Error fetching dealers: ', error);
                this.isLoadingDealers.set(false);
            },
        });
    }

    /**
     * On dealer search
     */
    protected async onDealerSearch(search: string): Promise<void> {
        this.isLoadingDealers.set(true);
        await new Promise((r) => setTimeout(r, 1000));
        this.isLoadingDealers.set(false);
    }

    /**
     * On remove selected dealer
     */
    protected removeDealer(myDealer: { label: string; value: string }): void {
        this.selectedDealers.update((dealers) => dealers.filter((d) => d.value !== myDealer.value));
    }

    /**
     * A public method to validate if there are selected dealers
     * @public so we can call this in the parent component `create-user.component.ts`
     */
    public validateSelectDealer(): boolean | null {
        this.hasSelectedDealers.set(this.selectedDealers().length > 0);
        return this.hasSelectedDealers();
    }

    /**
     * Method to save the current step data to the draft
     * @public so we can call this in the parent component `create-user.component.ts`
     */
    public saveInformation(): void {
        const userRole = this.createUserStepsService.selectedUserType()
            ?.id as SelectDealerStepRoles;
        this.createUserStepsService.updateStep(userRole, 'select-dealer', {
            selectedDealers: this.selectedDealers(),
        });
    }

    /**
     * Maps api response to UI requirements
     */
    private mapDataToUI(dealers: ApiDealers[]): { label: string; value: string }[] {
        return dealers.map((dealer) => ({ label: dealer.dealerName, value: dealer.dealerId }));
    }
}
